import { logFetchFailure } from '@shared/easyfy.fetchfailure';
import { getCookieByName, setCookie } from '@clientShared/utils';
import {
  Container,
  DecisionApiRequest,
  GetKevelAdPlacement,
  GetKevelAdPlacementRequest,
  GetPlacements,
  GetPlacementsRequest,
  KevelAdData,
  KevelPlaceholder,
  Placement,
  RenderedProductCardsCollection,
} from '@clientWebServices/kevel.service';
import { logCustomMessage } from '@shared/easyfy.logger';

export default class Kevel {
  static Instance: Kevel;
  public userId: string | null = null;
  private static COOKIE_NAME = 'media_uid';

  constructor() {
    if (!Kevel.Instance) {
      this.userId = this.ensureUserId();
      this.exposeSetUserIdToWindow();
      this.setInitialEmptyState();

      return (Kevel.Instance = this);
    }
    return Kevel.Instance;
  }

  private setInitialEmptyState(): void {
    const emptyPlaceholders = document.querySelectorAll('[data-kevel-placement][data-contains-data="false"]');
    emptyPlaceholders.forEach((placeholder) => {
      const flyoutContainer = placeholder.closest('.kevel-search-flyout-container');
      if (flyoutContainer) {
        flyoutContainer.classList.add('kevel-is-empty');
      }
    });
  }

  // #region Private Methods
  private ensureUserId(): string {
    const cookieUserId = getCookieByName(Kevel.COOKIE_NAME);
    if (cookieUserId) {
      return cookieUserId;
    }

    const workerUserId = this.getUserIdFromWorker();
    if (workerUserId) {
      return workerUserId;
    }

    const newUserId = crypto.randomUUID();
    return newUserId;
  }

  private getUserIdFromWorker(): string | undefined {
    return document.querySelector('[data-media-banner-identifier]')?.getAttribute('data-media-banner-identifier') ?? undefined;
  }

  private setUserIdCookie(userId: string): void {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1); // Add 1 year

    setCookie(userId, Kevel.COOKIE_NAME, date, 'Strict');
  }

  // Makes the setUserIdCookie method available globally so it can be triggered in OptanonWrapper after cookie consent is given
  private exposeSetUserIdToWindow(): void {
    (window as any).KevelSetUserIdCookie = () => {
      if (this.userId) Kevel.Instance.setUserIdCookie(this.userId);
    };
  }

  private toKevelPlaceholder = (div: HTMLElement): KevelPlaceholder | null => {
    const divName = div.dataset.kevelDivName;
    if (!divName) return null;

    const adTypes = this.getAdtypes(div);
    if (adTypes.length === 0) return null;

    return {
      listName: div.parentElement?.getAttribute('data-list-name') ?? null,
      listId: div.parentElement?.getAttribute('data-list-id') || null,
      divName,
      productCardType: Number(div.dataset.productCardType) || 0,
      adTypes,
      zones: this.getZones(div),
      count: this.getCount(div),
      element: div,
      keywords: this.getKeywords(div),
      isSlider: (div.dataset.isSlider ?? 'false').toLowerCase() === 'true',
      creativeSlot: div.dataset.creativeSlot,
    } as KevelPlaceholder;
  };

  private getAdtypes = (div: HTMLElement): number[] => {
    return (
      div.dataset.adtypes
        ?.split(',')
        .map((t) => parseInt(t.trim(), 10))
        .filter(Number.isFinite) ?? []
    );
  };

  private getKeywords = (div: HTMLElement): string[] => {
    return div.dataset.keywords?.split(',')?.map((k) => k?.trim()) ?? [];
  };

  private getZones = (div: HTMLElement): number[] => {
    return (
      div.dataset.zones
        ?.split(',')
        .map((t) => parseInt(t.trim(), 10))
        .filter(Number.isFinite) ?? []
    );
  };

  private getCount = (div: HTMLElement): number => Number(div.dataset.count) || 0;

  private createKevelRequestData = (kevelPlaceholders: KevelPlaceholder[]): GetPlacementsRequest => {
    const placements: Placement[] = kevelPlaceholders.map((div) => ({
      divName: div.divName,
      adTypes: div.adTypes,
      zoneIds: div.zones,
      count: div.count,
    }));

    const containers: Container[] = kevelPlaceholders.map((div) => ({
      listName: div.listName,
      listId: div.listId,
      divName: div.divName,
      productCardType: div.productCardType,
      isSlider: div.isSlider,
      count: div.count || 1,
      creativeSlot: div.creativeSlot,
    }));

    const requestData: DecisionApiRequest = {
      user: { key: this.userId ?? '' },
      consent: { gdpr: this.hasConsentedToCookie() },
      placements: placements,
      keywords: [...new Set(kevelPlaceholders.flatMap((div) => div.keywords))],
      url: window.location.pathname,
    };

    return { decisionApiRequest: requestData, containers };
  };

  private fetchPlacements = async (requestData: GetPlacementsRequest): Promise<RenderedProductCardsCollection[] | null> => {
    try {
      const response = await GetPlacements(requestData);
      if (response.status !== 200) {
        logCustomMessage('Response not success in fetchPlacements', {
          action: 'fetchPlacements',
          requestData: JSON.stringify(requestData),
          responseData: response.data,
          responseStatus: response.status,
          stack: new Error().stack ?? '',
        });
        return null;
      }
      return response.data?.productCardLists;
    } catch (error) {
      const failure = logFetchFailure('Error in fetchPlacements', { action: 'fetchPlacements' }, error);
      if (failure === 'unexpected') console.error('Error fetching placements:', error);
      return null;
    }
  };

  private fetchSingleAdPlacement = async (element: HTMLElement, slot: KevelAdPlacementSlot): Promise<KevelAdData | null> => {
    const divName = element.dataset.kevelDivName;
    if (!divName) return null;

    const adTypes = this.getAdtypes(element);
    if (adTypes.length === 0) return null;

    const keywords = this.getKeywords(element);
    const count = this.getCount(element) || 1;
    const zones = this.getZones(element);

    const placement: Placement = { divName, adTypes, zoneIds: zones, count };
    const requestData: DecisionApiRequest = {
      user: { key: this.userId ?? '' },
      consent: { gdpr: this.hasConsentedToCookie() },
      placements: [placement],
      keywords,
      url: window.location.pathname,
    };

    const request: GetKevelAdPlacementRequest = { decisionApiRequest: requestData, divName };

    try {
      const response = await GetKevelAdPlacement(slot, request);
      if (response.status !== 200 || !response.data) {
        return null;
      }

      return response.data as KevelAdData;
    } catch (error) {
      const action = `fetchKevelAdPlacement:${slot}`;
      logFetchFailure(`Error in ${action}`, { action }, error);
      return null;
    }
  };

  // #endregion

  //#region Public Methods
  // Fetches Kevel product cards for one placement element and inserts them into it. The element
  // does not have to be attached to the document. Returns true when it received content.
  fillPlacement = async (placement: HTMLElement): Promise<boolean> => {
    const kevelPlaceholder = this.toKevelPlaceholder(placement);
    if (!kevelPlaceholder) return false;

    const response = await this.fetchPlacements(this.createKevelRequestData([kevelPlaceholder]));
    const partials = response?.find((c) => c.containerId === kevelPlaceholder.divName)?.partials ?? [];
    if (partials.length === 0) return false;

    placement.insertAdjacentHTML('beforeend', partials.join(''));
    placement.setAttribute('data-contains-data', 'true');
    placement.closest('.kevel-search-flyout-container')?.classList.remove('kevel-is-empty');
    return true;
  };

  // Fills a copy of the placement and returns it, or null when it received no content. The copy keeps
  // the list container around the placement, since the request takes its list name from there. Without
  // a list name the server leaves the Kevel products out of the event layer, so they cannot be tracked.
  fillDetachedCopy = async (placement: HTMLElement): Promise<HTMLElement | null> => {
    const list = placement.parentElement?.cloneNode(false);
    const copy = placement.cloneNode(true) as HTMLElement;
    list?.appendChild(copy);

    return (await this.fillPlacement(copy)) ? copy : null;
  };

  getUserId(): string {
    return this.userId ?? this.ensureUserId();
  }

  hasConsentedToCookie(): boolean {
    return !!getCookieByName(Kevel.COOKIE_NAME);
  }

  fetchKevelAdPlacement = async (element: HTMLElement, slot: KevelAdPlacementSlot): Promise<KevelAdData | null> => {
    return this.fetchSingleAdPlacement(element, slot);
  };

  applyKevelAdData = (linkElement: HTMLElement, data: KevelAdData): void => {
    const image = linkElement.querySelector<HTMLImageElement>('[data-kevel-ad-image]');
    if (image && data.imageUrl) {
      image.src = data.imageUrl;
      image.alt = data.imageAltText ?? '';
    }

    if (data.linkUrl) {
      linkElement.setAttribute('href', data.linkUrl);
    }

    if (data.impressionUrl) {
      linkElement.setAttribute('data-event-property-impression-url', data.impressionUrl);
    }

    if (data.clickUrl) {
      linkElement.setAttribute('data-event-property-click-url', data.clickUrl);
    }

    if (data.promotionId) {
      linkElement.setAttribute('data-event-property-promotion-id', data.promotionId);
    }

    if (data.promotionName) {
      linkElement.setAttribute('data-event-property-promotion-name', data.promotionName);
    }

    const creativeSlot = linkElement.getAttribute('data-creative-slot');
    if (creativeSlot) {
      linkElement.setAttribute('data-event-property-creative-slot', creativeSlot);
    }

    linkElement.setAttribute('data-track-impression', 'true');
    linkElement.setAttribute('data-track-click', 'true');
  };
  //#endregion
}

export enum KevelContainerSelector {
  SearchFlyoutContainer = '[data-container="kevel-search-flyout-container"]',
  SearchFlyoutProducts = '[data-container="kevel-search-flyout-products"]',
  SearchFlyoutTemplate = '[data-container="kevel-search-flyout-template"]',
  SearchSuggestionAd = '[data-container="kevel-search-suggestion-ad"]',
}

export enum KevelAdPlacementSlot {
  FLOATING_AD_SLOT = 'floating',
  SEARCH_SUGGESTION_AD_SLOT = 'searchSuggestion',
}
